import { createContext, useContext, useMemo, useState } from "react";
import axios from "axios";

import { API_URL } from "./_lib/config";
import { useAdminContext } from "./admin/AdminContext";

const PageContext = createContext(null);

export const usePageContext = () => {
  const v = useContext(PageContext);
  if (!v) {
    throw new Error("usePageContext must be used inside PageProvider");
  }
  return v;
};

export const PageProvider = ({ children }) => {
  const [page, setPage] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventsCount, setEventsCount] = useState(null);
  const [attractions, setAttractions] = useState(null);
  const [attractionsCount, setAttractionsCount] = useState(null);
  const [categories, setCategories] = useState(null);
  const { sysAdmin } = useAdminContext();

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${sysAdmin?.token}` },
  });

  const safeCall = async (promise) => {
    let errorResponse = null;
    const response = await promise.catch((error) => {
      errorResponse = error?.response ?? null;
    });
    return response ?? errorResponse;
  };

  const updatePageState = async (next) => setPage(next);

  const fetchPages = async (p = 1, pageSize = 50) => {
    const response = await safeCall(
      axios.get(`${API_URL}/api/v1/page/?page=${p}&pageSize=${pageSize}`),
    );
    const payload = response?.data?.data;
    if (payload) {
      setPage(payload.pages);
      setCategories(payload.categories);
    }
    return response;
  };

  const fetchEvents = async (p = 1, pageSize = 50) => {
    const response = await safeCall(
      axios.get(
        `${API_URL}/api/v1/admin/event/list/?page=${p}&pageSize=${pageSize}`,
        authConfig(),
      ),
    );
    const payload = response?.data?.data;
    if (payload) {
      setEvents(payload.data);
      setEventsCount(payload.count);
    }
    return response;
  };

  const fetchAttractions = async (p = 1, pageSize = 50) => {
    const response = await safeCall(
      axios.get(
        `${API_URL}/api/v1/attraction/?page=${p}&pageSize=${pageSize}`,
        authConfig(),
      ),
    );
    const payload = response?.data?.data;
    if (payload) {
      setAttractions(payload.data);
      setAttractionsCount(payload.count);
    }
    return response;
  };

  const getCategories = async () => {
    const response = await safeCall(
      axios.get(`${API_URL}/api/v1/page/categories`),
    );
    if (response?.data?.data) setCategories(response.data.data);
    return response;
  };

  const createOnePage = async (data) => {
    const response = await safeCall(
      axios.post(`${API_URL}/api/v1/admin/page/create`, data, authConfig()),
    );
    if (response) getCategories();
    return response;
  };

  const updatePage = async (data) =>
    safeCall(
      axios.put(`${API_URL}/api/v1/admin/page/update`, data, authConfig()),
    );

  const deletePage = async (page_id) =>
    safeCall(
      axios.delete(`${API_URL}/api/v1/admin/page/delete/${page_id}`, authConfig()),
    );

  const value = useMemo(
    () => ({
      page,
      categories,
      events,
      eventsCount,
      attractions,
      attractionsCount,
      fetchPages,
      fetchEvents,
      fetchAttractions,
      getCategories,
      createOnePage,
      updatePage,
      deletePage,
      updatePageState,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, categories, events, eventsCount, attractions, attractionsCount, sysAdmin?.token],
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
};
